import {
  Button,
  MainNavigation,
  MainNavigationItem,
  SubNavigation,
  SubNavigationContent,
  SubNavigationGroup,
  SubNavigationHeader,
  SubNavigationItem,
  SubNavigationList,
  SubNavigationTitle,
  Link,
  MainNavigationProps
} from '@wfp/ui';
import { T, useT } from '@transifex/react';
import { useEnv } from '../../../context/EnvironmentContext/EnvironmentContext';
import { useOnlineStatus } from '../../../context/OnlineStatusContext/OnlineStatusContext';
import { OfflineBanner } from '../OfflineBanner/OfflineBanner';
import { useNavbar } from './Navbar.hooks';
import './Navbar.scss';
import {
  App,
  AppBaseHref
} from '@partnerconnect-monorepo/types/EnvironmentVariables';
import { UserProfileDetails } from './UserProfileDetails/UserProfileDetails.connect';
import { UserImpersonationControlBar } from '../UserImpersonationControlBar';
import { navigateToUrl } from '../../../utils/navigation';

export const Navbar = () => {
  const { onlineStatus } = useOnlineStatus();
  const { deployedOnRootPath, appName } = useEnv();
  const t = useT();
  const {
    setLocale,
    languageCode,
    languages,
    signinRedirect,
    canAccessInvoicing,
    canAccessPartnershipsAndFinancesUsers,
    canAccessReportingUsers,
    isUsersManagementAccessed,
    canAccessReporting,
    canAccessPartnershipOpportunities,
    canAccessSpotcheck,
    isPartnershipsModuleAccessed,
    isAuthenticated,
    isPartnershipsOverviewPageEnabled
  } = useNavbar();

  return (
    <>
      <MainNavigation
        id="navbar"
        mobilePageWidth="full"
        pageWidth="lg"
        logo={
          <div className="navbar navbar__link">
            <Link
              href={AppBaseHref.Home}
              onClick={(e) => {
                if (appName !== App.Home) {
                  e.preventDefault();
                  navigateToUrl('/');
                }
              }}
            >
              <h3>
                <T _str="Partner Connect" _tag="navbar" />
              </h3>
            </Link>
          </div>
        }
      >
        {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          (props: MainNavigationProps) => (
            <>
              {isAuthenticated ? (
                deployedOnRootPath &&
                appName !== App.Home && (
                  <MainNavigationItem>
                    <Link
                      href="/"
                      className={`navbar__link--active ${
                        !onlineStatus ? 'offline-link' : ''
                      }`}
                    >
                      <T _str={appName} _tag="navbar" />
                    </Link>
                  </MainNavigationItem>
                )
              ) : (
                <MainNavigationItem>
                  <Button
                    small
                    kind="navigation"
                    onClick={() => signinRedirect()}
                  >
                    <T _str="Login" _tag="navbar" />
                  </Button>
                </MainNavigationItem>
              )}
              {canAccessReporting && (
                <MainNavigationItem
                  className={`wfp--main-navigation__user sub-navigation-item ${
                    !onlineStatus ? 'offline-link' : ''
                  }`}
                  subNavigation={
                    <SubNavigation>
                      <SubNavigationContent>
                        <SubNavigationList>
                          <SubNavigationItem>
                            <Link
                              href={AppBaseHref.Reporting}
                              onClick={(e) => {
                                if (appName !== App.Reporting) {
                                  e.preventDefault();
                                  navigateToUrl(AppBaseHref.Reporting);
                                }
                              }}
                            >
                              <T _str="Reporting overview" _tag="navbar" />
                            </Link>
                          </SubNavigationItem>
                          <SubNavigationItem>
                            <Link
                              href={`${AppBaseHref.Reporting}distribution-reports/`}
                              onClick={(e) => {
                                if (appName !== App.Reporting) {
                                  e.preventDefault();
                                  navigateToUrl(
                                    `${AppBaseHref.Reporting}distribution-reports/`
                                  );
                                }
                              }}
                            >
                              <T _str="Distribution reports" _tag="navbar" />
                            </Link>
                          </SubNavigationItem>
                        </SubNavigationList>
                      </SubNavigationContent>
                    </SubNavigation>
                  }
                >
                  <Link
                    href={AppBaseHref.Reporting}
                    {...(appName === App.Reporting &&
                      !isUsersManagementAccessed && {
                        className: 'navbar__link--active'
                      })}
                  >
                    <T _str="Reports" _tag="navbar" />
                  </Link>
                </MainNavigationItem>
              )}
              {(canAccessPartnershipOpportunities || canAccessSpotcheck) && (
                <MainNavigationItem
                  className={`wfp--main-navigation__user sub-navigation-item ${
                    !onlineStatus ? 'offline-link' : ''
                  }`}
                  subNavigation={
                    <SubNavigation>
                      <SubNavigationContent>
                        <SubNavigationList>
                          {canAccessPartnershipOpportunities && (
                            <>
                              {isPartnershipsOverviewPageEnabled && (
                                <SubNavigationItem>
                                  <Link
                                    href={AppBaseHref.Partnerships}
                                    onClick={(e) => {
                                      if (appName !== App.Partnerships) {
                                        e.preventDefault();
                                        navigateToUrl(AppBaseHref.Partnerships);
                                      }
                                    }}
                                  >
                                    <T
                                      _str="Partnerships overview"
                                      _tag="navbar"
                                    />
                                  </Link>
                                </SubNavigationItem>
                              )}
                              <SubNavigationItem>
                                <Link
                                  href={`${AppBaseHref.Partnerships}opportunities/`}
                                  onClick={(e) => {
                                    if (appName !== App.Partnerships) {
                                      e.preventDefault();
                                      navigateToUrl(
                                        `${AppBaseHref.Partnerships}opportunities/`
                                      );
                                    }
                                  }}
                                >
                                  <T _str="Opportunities" _tag="navbar" />
                                </Link>
                              </SubNavigationItem>
                            </>
                          )}
                          {canAccessSpotcheck && (
                            <SubNavigationItem>
                              <Link
                                href={AppBaseHref.Spotcheck}
                                onClick={(e) => {
                                  if (appName !== App.Spotcheck) {
                                    e.preventDefault();
                                    navigateToUrl(AppBaseHref.Spotcheck);
                                  }
                                }}
                              >
                                <T _str="Spot-checks" _tag="navbar" />
                              </Link>
                            </SubNavigationItem>
                          )}
                        </SubNavigationList>
                      </SubNavigationContent>
                    </SubNavigation>
                  }
                >
                  <Link
                    {...(isPartnershipsModuleAccessed &&
                      !isUsersManagementAccessed && {
                        className: 'navbar__link--active'
                      })}
                  >
                    <T _str="Partnerships" _tag="navbar" />
                  </Link>
                </MainNavigationItem>
              )}
              {canAccessInvoicing && (
                <MainNavigationItem>
                  <Link
                    href={AppBaseHref.Invoicing}
                    className={`${
                      appName === App.Invoicing ? 'navbar__link--active' : ''
                    } ${!onlineStatus ? 'offline-link' : ''}`}
                    onClick={(e) => {
                      if (appName !== App.Invoicing) {
                        e.preventDefault();
                        navigateToUrl(AppBaseHref.Invoicing);
                      }
                    }}
                  >
                    <T _str="Finances" _tag="navbar" />
                  </Link>
                </MainNavigationItem>
              )}
              {(canAccessReportingUsers ||
                canAccessPartnershipsAndFinancesUsers) && (
                <MainNavigationItem
                  className="wfp--main-navigation__user sub-navigation-item"
                  subNavigation={
                    <SubNavigation>
                      <SubNavigationContent>
                        <SubNavigationList>
                          {canAccessReportingUsers && (
                            <SubNavigationItem>
                              <Link
                                href={`${AppBaseHref.Reporting}users/`}
                                onClick={(e) => {
                                  if (appName !== App.Reporting) {
                                    e.preventDefault();
                                    navigateToUrl(
                                      `${AppBaseHref.Reporting}users/`
                                    );
                                  }
                                }}
                              >
                                <T _str="Reporting" />
                              </Link>
                            </SubNavigationItem>
                          )}
                          {canAccessPartnershipsAndFinancesUsers && (
                            <SubNavigationItem>
                              <Link
                                href={`${AppBaseHref.Partnerships}users/`}
                                onClick={(e) => {
                                  if (appName !== App.Partnerships) {
                                    e.preventDefault();
                                    navigateToUrl(
                                      `${AppBaseHref.Partnerships}users/`
                                    );
                                  }
                                }}
                              >
                                <T _str="Partnerships, Finances" />
                              </Link>
                            </SubNavigationItem>
                          )}
                        </SubNavigationList>
                      </SubNavigationContent>
                    </SubNavigation>
                  }
                >
                  <Link
                    {...(isUsersManagementAccessed && {
                      className: 'navbar__link--active'
                    })}
                  >
                    <T _str="Users" _tag="navbar" />
                  </Link>
                </MainNavigationItem>
              )}
              {isAuthenticated && <UserProfileDetails {...props} />}
              {isAuthenticated && (
                <MainNavigationItem>
                  <a href="/support">
                    <T _str="Help" />
                  </a>
                </MainNavigationItem>
              )}
              <MainNavigationItem
                className="wfp--main-navigation__user"
                data-testid="navbar-user-container"
                subNavigation={
                  <SubNavigation>
                    <SubNavigationHeader>
                      <SubNavigationTitle>
                        {t('Choose language', { _tag: 'navbar' })}
                      </SubNavigationTitle>
                    </SubNavigationHeader>
                    <SubNavigationContent>
                      <SubNavigationList>
                        <SubNavigationGroup>
                          {languages.map(
                            ({ code, localized_name }, languageIdx) => (
                              <SubNavigationItem key={languageIdx}>
                                <Button
                                  kind="ghost"
                                  small
                                  onClick={() => setLocale(code)}
                                >
                                  {localized_name}
                                </Button>
                              </SubNavigationItem>
                            )
                          )}
                        </SubNavigationGroup>
                      </SubNavigationList>
                    </SubNavigationContent>
                  </SubNavigation>
                }
              >
                <span className="locale">{languageCode}</span>
              </MainNavigationItem>
            </>
          )
        }
      </MainNavigation>
      {!onlineStatus && <OfflineBanner />}
      <UserImpersonationControlBar />
    </>
  );
};
